import { useEffect, useRef, useState, useCallback } from 'react'
import * as Y from 'yjs'
import { HocuspocusProvider } from '@hocuspocus/provider'
import type { UserPresence, ChatMessage, Idea } from '../types'
import { generateId } from '../lib/id'

const WS_URL = import.meta.env.VITE_WS_URL || 'ws://localhost/collab//'

export interface UseCollaborationReturn {
  yDoc: Y.Doc
  provider: HocuspocusProvider | null
  connected: boolean
  synced: boolean
  /**
   * True quando este cliente é o "líder" da sala (menor clientID no awareness).
   * Usado para eleger um único disparador do run automático dos agentes —
   * evita que todos os clientes iniciem o pipeline ao mesmo tempo.
   */
  isLeader: boolean
  users: UserPresence[]
  sendCursor: (x: number, y: number) => void
  sendChatMessage: (content: string, sender?: { id: string; name: string }) => void
  sendIdea: (idea: Idea) => void
  ideas: Y.Array<Idea>
  chatMessages: Y.Array<ChatMessage>
  canvasElements: Y.Map<unknown>
  artifacts: Y.Map<Record<string, unknown>>
  eventStormElements: Y.Array<Record<string, unknown>>
}

function generateColorFromId(id: string): string {
  let hash = 0
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash)
  }
  const hue = Math.abs(hash % 360)
  return `hsl(${hue}, 70%, 50%)`
}

export function useCollaboration(
  roomId: string,
  jwtToken: string,
  currentUser?: { id: string; name: string; email: string } | null
): UseCollaborationReturn {
  const yDocRef = useRef<Y.Doc>(new Y.Doc())
  const providerRef = useRef<HocuspocusProvider | null>(null)
  const [connected, setConnected] = useState(false)
  const [synced, setSynced] = useState(false)
  const [users, setUsers] = useState<UserPresence[]>([])
  // Sem awareness ainda (solo/offline) assumimos liderança; o lock Redis do
  // backend continua sendo o guarda final contra runs concorrentes.
  const [isLeader, setIsLeader] = useState(true)

  const yDoc = yDocRef.current
  const ideas = yDoc.getArray<Idea>('ideas')
  const chatMessages = yDoc.getArray<ChatMessage>('chatMessages')
  const canvasElements = yDoc.getMap('canvasElements')
  const artifacts = yDoc.getMap<Record<string, unknown>>('artifacts')
  const eventStormElements = yDoc.getArray<Record<string, unknown>>('eventStormElements')

  useEffect(() => {
    if (!roomId || !jwtToken) return

    console.log('[useCollaboration] Connecting to WS_URL:', WS_URL, 'roomId:', roomId)

    const prov = new HocuspocusProvider({
      url: WS_URL,
      name: roomId,
      // Função: a cada reconexão o Hocuspocus valida o `exp`, então lemos o token renovado.
      token: () => localStorage.getItem('token') || jwtToken,
      document: yDoc,
      onConnect: () => setConnected(true),
      onDisconnect: () => {
        setConnected(false)
        setSynced(false)
      },
      onSynced: () => setSynced(true),
      onStatus: ({ status }) => {
        setConnected(status === 'connected')
      },
      onAwarenessUpdate: ({ states }) => {
        const presenceList: UserPresence[] = []
        const clientIds: number[] = []
        states.forEach((state: Record<string, unknown>) => {
          const userState = state.user as Record<string, unknown> | undefined
          if (userState) {
            const uid = String(userState.id || '')
            if (typeof state.clientId === 'number') clientIds.push(state.clientId)
            presenceList.push({
              id: uid,
              name: String(userState.name || 'Anônimo'),
              email: String(userState.email || ''),
              color: String(userState.color || generateColorFromId(uid)),
              cursor: (userState.cursor as { x: number; y: number }) || undefined,
              status: (userState.status as 'online' | 'away') || 'online',
            })
          }
        })
        // Eleição de líder: menor clientID entre os presentes. Determinístico —
        // todos os clientes chegam à mesma conclusão sem coordenação extra.
        setIsLeader(clientIds.length === 0 || Math.min(...clientIds) === yDoc.clientID)
        // Only update state if the list actually changed
        setUsers((prev) => {
          if (prev.length !== presenceList.length) return presenceList
          for (let i = 0; i < prev.length; i++) {
            if (prev[i].id !== presenceList[i].id) return presenceList
          }
          return prev
        })
      },
    })

    providerRef.current = prov

    if (currentUser && prov.awareness) {
      prov.awareness.setLocalStateField('user', {
        id: currentUser.id,
        name: currentUser.name,
        email: currentUser.email,
        color: generateColorFromId(currentUser.id),
        status: 'online',
        cursor: { x: 0, y: 0 },
      })
    }

    return () => {
      prov.destroy()
      providerRef.current = null
      setConnected(false)
      setSynced(false)
      setUsers([])
    }
  }, [roomId, jwtToken])

  // Update awareness when currentUser changes
  useEffect(() => {
    const prov = providerRef.current
    if (currentUser && prov?.awareness) {
      prov.awareness.setLocalStateField('user', {
        id: currentUser.id,
        name: currentUser.name,
        email: currentUser.email,
        color: generateColorFromId(currentUser.id),
        status: 'online',
        cursor: { x: 0, y: 0 },
      })
    }
  }, [currentUser])

  const sendCursor = useCallback(
    (x: number, y: number) => {
      const prov = providerRef.current
      if (prov?.awareness) {
        prov.awareness.setLocalStateField('user', {
          ...(prov.awareness.getLocalState()?.user || {}),
          cursor: { x, y },
        })
      }
    },
    []
  )

  const sendChatMessage = useCallback(
    (content: string, sender?: { id: string; name: string }) => {
      const msg: ChatMessage = {
        id: generateId(),
        content,
        sender_id: sender?.id || currentUser?.id || 'unknown',
        sender_name: sender?.name || currentUser?.name || 'Anônimo',
        sender_type: 'user',
        created_at: new Date().toISOString(),
      }
      chatMessages.push([msg])
    },
    [chatMessages, currentUser]
  )

  const sendIdea = useCallback(
    (idea: Idea) => {
      ideas.push([idea])
    },
    [ideas]
  )

  return {
    yDoc,
    provider: providerRef.current,
    connected,
    synced,
    isLeader,
    users,
    sendCursor,
    sendChatMessage,
    sendIdea,
    ideas,
    chatMessages,
    canvasElements,
    artifacts,
    eventStormElements,
  }
}
